/**
 * OwnershipVerificationStep — the listing wizard's "Confirm Ownership" step.
 *
 * The seller declares their capacity (owner / co-owner / trustee / …), lists
 * any other owners, uploads one proof document (deed, tax bill, owner's title
 * policy) plus authority documents when acting for someone else, and attests.
 * The server extracts + matches synchronously and answers with a status:
 *
 *   auto_approved / approved → verified, continue
 *   needs_review             → continue, but publishing waits for staff review
 *   more_info_requested      → staff note + re-upload
 *   revoked                  → address changed; re-upload
 *   rejected                 → staff note; blocked
 *   processing               → still working; "Check again"
 *
 * State ownership: the parent (ListingForm) holds the VerificationStatus
 * because it gates Continue / Publish; this component owns the form and the
 * submit request and reports the new status through `onStatusChange`.
 *
 * Copy: docs/plans/ownership-verification/legal-review.md §5 — verbatim.
 *
 * @param {object} props
 * @param {number|string|null} props.houseId - null while the draft is saving.
 * @param {object|null} props.status - VerificationStatus from the server.
 * @param {boolean} props.loading - status request in flight.
 * @param {string|null} props.loadError - status request failure.
 * @param {() => void} props.onRefresh - re-fetch the status.
 * @param {(status: object) => void} props.onStatusChange - after a submit.
 * @param {boolean} [props.grandfathered] - the gate doesn't apply (listing was live
 *   before this check existed, or the server says it isn't required).
 * @param {boolean} [props.previouslyPublished] - listing has been published before;
 *   a rejection then only removes the buyer badge (no refund, no unpublish).
 */
import React, { useId, useMemo, useRef, useState } from 'react';
import { ownershipVerificationAgent } from '../../agents/ownershipVerificationAgent';
import { LEGAL_DISCLAIMER } from '../../utils/disclaimers';
import {
  ACCEPT_ATTRIBUTE,
  AUTHORITY_DOCUMENT_TYPES,
  AUTHORITY_REQUIREMENTS,
  CAPACITY_DETAIL_HINTS,
  CAPACITY_LABELS,
  CAPACITY_OPTIONS,
  DEFAULT_AUTHORITY_TYPE,
  MAX_AUTHORITY_FILES,
  MAX_FILE_MB,
  OWNERSHIP_ATTESTATION_VERSION,
  POA_CONFIRMATION_TEXT,
  PROOF_DOCUMENT_TYPES,
  SELLER_ATTESTATION_TEXT,
  SELLER_DOCUMENT_HANDLING,
  SELLER_HOW_WE_CHECK,
  SELLER_PROTECT_INFO,
  SELLER_UPLOAD_HEADING,
  SELLER_UPLOAD_INTRO,
  SUBMITTABLE_STATUSES,
  documentTypeLabel,
  isVerifiedStatus,
  needsAuthority,
  validateOwnershipFile,
} from '../../utils/ownershipVerification';
import styles from './OwnershipVerificationStep.module.css';

const MAX_OTHER_OWNERS = 10;

/** Seller-facing copy for a submit failure. */
export function submitErrorMessage(err) {
  const status = err?.status ?? err?.statusCode;
  const code = err?.detail?.code;
  if (code === 'invalid_file_type' || status === 400) {
    return err?.detail?.message || 'One of your files is not a PDF, JPEG or PNG. Choose a different file and try again.';
  }
  // Whole multipart request over the server cap (60 MB), vs one file over 10 MB.
  if (code === 'request_too_large') {
    return `Your files are too large together. Upload fewer or smaller files (${MAX_FILE_MB} MB each).`;
  }
  if (code === 'file_too_large' || status === 413) {
    return `One of your files is larger than ${MAX_FILE_MB} MB. Upload only the pages that show the owner, address and parcel number.`;
  }
  if (code === 'verification_locked' || status === 409) {
    return 'Your documents are already submitted. Refresh to see the latest status.';
  }
  if (status === 403) {
    return 'Only the seller of this listing can verify its ownership.';
  }
  if (code === 'poa_confirmation_required') {
    return 'Confirm that the power of attorney has not been revoked and that the owner you act for is living.';
  }
  if (status === 422) {
    return err?.message || 'Some details are missing or invalid. Check the form and try again.';
  }
  if (err?.isTimeout) return err.message;
  return err?.message || "We couldn't upload your documents. Please try again.";
}

/** Live-region announcement for a fresh submit result. */
function resultAnnouncement(status) {
  switch (status) {
    case 'auto_approved':
    case 'approved':
      return 'Documents reviewed. You can continue.';
    case 'needs_review':
      return 'Documents received. A cutthe6.com staff member will review them. You can continue, but you cannot publish until the review is complete.';
    case 'processing':
      return 'Documents received. We are still processing them.';
    case 'rejected':
      return "We couldn't confirm your ownership or authority from the documents provided.";
    case 'more_info_requested':
      return 'We need more information to finish reviewing your documents.';
    default:
      return 'Documents submitted.';
  }
}

let rowSeq = 0;
const newAuthorityRow = (capacity) => ({
  id: `auth-${(rowSeq += 1)}`,
  documentType: DEFAULT_AUTHORITY_TYPE[capacity] || 'other',
  file: null,
});

/** Paragraph with a bold lead-in, as in legal-review.md §5. */
function LeadParagraph({ lead, children }) {
  return (
    <p className={styles.copy}>
      <strong>{lead}</strong> {children}
    </p>
  );
}

function SubmittedDocuments({ documents }) {
  if (!Array.isArray(documents) || documents.length === 0) return null;
  return (
    <ul className={styles.docList} aria-label="Documents you submitted">
      {documents.map((d) => (
        <li key={d.id} className={styles.docItem}>
          <span className={styles.docKind}>{d.kind === 'authority' ? 'Authority' : 'Proof'}</span>
          <span className={styles.docType}>{documentTypeLabel(d.document_type)}</span>
          {d.original_filename && <span className={styles.docName}>{d.original_filename}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Status panel shown above (or instead of) the form. */
function StatusPanel({ status, onRefresh, loading, previouslyPublished }) {
  const s = status?.status;
  const capacity = CAPACITY_LABELS[status?.capacity];
  if (isVerifiedStatus(s)) {
    return (
      <div className={`${styles.panel} ${styles.panelSuccess}`} data-testid="ownership-status-verified">
        <span className={styles.seal} aria-hidden="true">✓</span>
        <div>
          <p className={styles.panelTitle}>Ownership documents reviewed</p>
          <p className={styles.panelText}>
            {capacity ? `Your role: ${capacity}. ` : ''}You can continue to the next step.
          </p>
          <SubmittedDocuments documents={status.documents} />
        </div>
      </div>
    );
  }
  if (s === 'needs_review') {
    return (
      <div className={`${styles.panel} ${styles.panelInfo}`} data-testid="ownership-status-needs-review">
        <span className={styles.seal} aria-hidden="true">⌛</span>
        <div>
          <p className={styles.panelTitle}>Your documents are under review</p>
          <p className={styles.panelText}>
            A cutthe6.com staff member will review your documents. You can keep
            working on your listing, but it can&apos;t be published until the
            review is complete.
          </p>
          <SubmittedDocuments documents={status.documents} />
        </div>
      </div>
    );
  }
  if (s === 'processing') {
    return (
      <div className={`${styles.panel} ${styles.panelInfo}`} data-testid="ownership-status-processing">
        <span className={styles.seal} aria-hidden="true">⌛</span>
        <div>
          <p className={styles.panelTitle}>We&apos;re still reading your document</p>
          <p className={styles.panelText}>This usually takes less than a minute.</p>
          <button type="button" className={styles.secondaryBtn} onClick={onRefresh} disabled={loading}>
            {loading ? 'Checking…' : 'Check again'}
          </button>
        </div>
      </div>
    );
  }
  if (s === 'rejected') {
    return (
      <div className={`${styles.panel} ${styles.panelError}`} data-testid="ownership-status-rejected" role="alert">
        <span className={styles.seal} aria-hidden="true">!</span>
        <div>
          <p className={styles.panelTitle}>We couldn&apos;t confirm your ownership or authority to sell this property</p>
          {status.seller_message && (
            <blockquote className={styles.staffNote} data-testid="ownership-seller-message">
              {status.seller_message}
            </blockquote>
          )}
          {previouslyPublished ? (
            // Policy: rejecting a previously-published listing removes the
            // badge only — no refund, no unpublish.
            <p className={styles.panelText} data-testid="ownership-rejected-published">
              We couldn&apos;t confirm your ownership or authority from the documents
              provided, so this listing won&apos;t show the &quot;Ownership documents
              reviewed&quot; badge. Your listing stays published. If you think this is
              a mistake, contact cutthe6.com support.
            </p>
          ) : (
            <p className={styles.panelText} data-testid="ownership-rejected-unpublished">
              This listing can&apos;t be published. We&apos;re refunding any Full Service
              fee you paid for this listing, in full, to your original payment method.
              Refunds usually appear within 5–10 business days. If you think this is
              a mistake, contact cutthe6.com support.
            </p>
          )}
        </div>
      </div>
    );
  }
  if (s === 'more_info_requested') {
    return (
      <div className={`${styles.panel} ${styles.panelWarning}`} data-testid="ownership-status-more-info" role="status">
        <span className={styles.seal} aria-hidden="true">?</span>
        <div>
          <p className={styles.panelTitle}>We need a little more information</p>
          {status.seller_message && (
            <blockquote className={styles.staffNote} data-testid="ownership-seller-message">
              {status.seller_message}
            </blockquote>
          )}
          <p className={styles.panelText}>Upload the requested documents below and submit again.</p>
        </div>
      </div>
    );
  }
  if (s === 'revoked') {
    return (
      <div className={`${styles.panel} ${styles.panelWarning}`} data-testid="ownership-status-revoked" role="status">
        <span className={styles.seal} aria-hidden="true">↻</span>
        <div>
          <p className={styles.panelTitle}>Please upload your ownership documents again</p>
          {/* System revocations explain themselves (account name or listing
              address changed); admin revocations carry the admin's note. */}
          {status.seller_message ? (
            <blockquote className={styles.staffNote} data-testid="ownership-seller-message">
              {status.seller_message}
            </blockquote>
          ) : (
            <p className={styles.panelText}>
              Something changed after your documents were reviewed, so we need to review them again.
            </p>
          )}
          <p className={styles.panelText}>Upload your documents below and submit again.</p>
        </div>
      </div>
    );
  }
  return null;
}

export default function OwnershipVerificationStep({
  houseId,
  status,
  loading = false,
  loadError = null,
  onRefresh,
  onStatusChange,
  grandfathered = false,
  previouslyPublished = false,
}) {
  const uid = useId();
  const [capacity, setCapacity] = useState(status?.capacity || '');
  const [capacityDetail, setCapacityDetail] = useState(status?.capacity_detail || '');
  const [otherOwners, setOtherOwners] = useState(
    Array.isArray(status?.other_owners) ? status.other_owners : [],
  );
  const [proofType, setProofType] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [authorityRows, setAuthorityRows] = useState([]);
  const [attested, setAttested] = useState(false);
  // Second, POA-only confirmation (legal, 2026-09-29). Unchecked by default.
  const [poaConfirmed, setPoaConfirmed] = useState(false);
  const [fileErrors, setFileErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const proofInputRef = useRef(null);

  const currentStatus = status?.status || 'not_started';
  const canSubmitStatus = SUBMITTABLE_STATUSES.includes(currentStatus);
  const authorityNeeded = needsAuthority(capacity);
  const isPoa = capacity === 'power_of_attorney';

  const handleCapacityChange = (value) => {
    setCapacity(value);
    setPoaConfirmed(false);
    if (needsAuthority(value)) {
      setAuthorityRows((rows) => (rows.length > 0 ? rows : [newAuthorityRow(value)]));
    } else {
      setAuthorityRows([]);
      setCapacityDetail('');
    }
  };

  const setFileError = (key, message) =>
    setFileErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });

  const handleProofFile = (file) => {
    const problem = file ? validateOwnershipFile(file) : null;
    setFileError('proof', problem);
    setProofFile(problem ? null : file || null);
    if (problem && proofInputRef.current) proofInputRef.current.value = '';
  };

  const handleAuthorityFile = (rowId, file, inputEl) => {
    const problem = file ? validateOwnershipFile(file) : null;
    setFileError(rowId, problem);
    if (problem && inputEl) inputEl.value = '';
    setAuthorityRows((rows) =>
      rows.map((r) => (r.id === rowId ? { ...r, file: problem ? null : file || null } : r)),
    );
  };

  // Problems that block submit, in form order. Shown after the first attempt.
  const validationProblems = useMemo(() => {
    const problems = [];
    if (!capacity) problems.push('Choose how you are connected to this property.');
    if (authorityNeeded && !capacityDetail.trim()) {
      problems.push(`Enter the ${(CAPACITY_DETAIL_HINTS[capacity]?.label || 'details').toLowerCase()}.`);
    }
    if (!proofType) problems.push('Choose the type of ownership document.');
    if (!proofFile) problems.push('Choose the ownership document to upload.');
    if (authorityNeeded && !authorityRows.some((r) => r.file)) {
      problems.push('Upload at least one document that shows your authority to sell.');
    }
    if (isPoa && !poaConfirmed) problems.push('Check the power of attorney confirmation box.');
    if (!attested) problems.push('Check the confirmation box.');
    return problems;
  }, [capacity, authorityNeeded, capacityDetail, proofType, proofFile, authorityRows, attested, isPoa, poaConfirmed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    setSubmitError(null);
    if (validationProblems.length > 0 || !houseId || submitting) return;
    setSubmitting(true);
    setAnnouncement('Uploading and checking your documents…');
    try {
      const result = await ownershipVerificationAgent.submit(houseId, {
        capacity,
        capacityDetail: authorityNeeded ? capacityDetail : '',
        otherOwners,
        proofDocumentType: proofType,
        proofFile,
        authorityDocuments: authorityNeeded ? authorityRows.filter((r) => r.file) : [],
        attestationAccepted: attested,
        poaConfirmation: isPoa ? poaConfirmed : undefined,
        attestationVersion: OWNERSHIP_ATTESTATION_VERSION,
      });
      setAnnouncement(resultAnnouncement(result?.status));
      // A fresh form for any later re-upload (more info requested, revoked).
      setProofFile(null);
      setAuthorityRows((rows) => rows.map((r) => ({ ...r, file: null })));
      setAttested(false);
      setPoaConfirmed(false);
      setShowErrors(false);
      if (onStatusChange) onStatusChange(result);
    } catch (err) {
      console.error('Ownership verification submit failed:', err?.status);
      const message = submitErrorMessage(err);
      setSubmitError(message);
      setAnnouncement(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  const liveRegion = (
    <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true" data-testid="ownership-announcer">
      {announcement}
    </div>
  );

  if (!houseId) {
    return (
      <div className={styles.step} data-testid="ownership-step">
        <h3 className={styles.title}>{SELLER_UPLOAD_HEADING}</h3>
        <p className={styles.muted} role="status">Saving your listing draft so your documents can be attached…</p>
      </div>
    );
  }

  if (!status && loading) {
    return (
      <div className={styles.step} data-testid="ownership-step">
        <h3 className={styles.title}>{SELLER_UPLOAD_HEADING}</h3>
        <p className={styles.muted} role="status" data-testid="ownership-loading">
          <span className={styles.spinner} aria-hidden="true" /> Loading verification status…
        </p>
      </div>
    );
  }

  if (!status && loadError) {
    return (
      <div className={styles.step} data-testid="ownership-step">
        <h3 className={styles.title}>{SELLER_UPLOAD_HEADING}</h3>
        <div className={`${styles.panel} ${styles.panelError}`} role="alert" data-testid="ownership-load-error">
          <div>
            <p className={styles.panelText}>{loadError}</p>
            <button type="button" className={styles.secondaryBtn} onClick={onRefresh}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ids = {
    capacity: `${uid}-capacity`,
    detail: `${uid}-detail`,
    proofType: `${uid}-proof-type`,
    proofFile: `${uid}-proof-file`,
    attest: `${uid}-attest`,
    poa: `${uid}-poa`,
    problems: `${uid}-problems`,
  };
  const detailHint = CAPACITY_DETAIL_HINTS[capacity];

  return (
    <div className={styles.step} data-testid="ownership-step">
      {liveRegion}
      <h3 className={styles.title}>{SELLER_UPLOAD_HEADING}</h3>

      {grandfathered && !isVerifiedStatus(currentStatus) && (
        <p className={styles.grandfathered} data-testid="ownership-grandfathered">
          This listing was published before this ownership check was required,
          so this step is optional for it.
        </p>
      )}

      <StatusPanel
        status={status}
        onRefresh={onRefresh}
        loading={loading}
        previouslyPublished={previouslyPublished}
      />

      {canSubmitStatus && (
        <form className={styles.form} onSubmit={handleSubmit} noValidate aria-describedby={showErrors && validationProblems.length ? ids.problems : undefined}>
          <p className={styles.copy}>{SELLER_UPLOAD_INTRO}</p>

          {/* 1 — Capacity */}
          <fieldset className={styles.group}>
            <legend className={styles.groupTitle}><span className={styles.num} aria-hidden="true">1</span> Your connection to the property</legend>
            <label className={styles.label} htmlFor={ids.capacity}>How are you connected to this property? *</label>
            <select
              id={ids.capacity}
              className={styles.select}
              value={capacity}
              onChange={(e) => handleCapacityChange(e.target.value)}
              aria-invalid={showErrors && !capacity ? 'true' : undefined}
              data-testid="ownership-capacity"
            >
              <option value="">Choose one…</option>
              {CAPACITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {authorityNeeded && (
              <>
                <label className={styles.label} htmlFor={ids.detail}>{detailHint?.label || 'Details'} *</label>
                <input
                  id={ids.detail}
                  className={styles.input}
                  type="text"
                  maxLength={255}
                  value={capacityDetail}
                  placeholder={detailHint?.placeholder}
                  onChange={(e) => setCapacityDetail(e.target.value)}
                  aria-invalid={showErrors && !capacityDetail.trim() ? 'true' : undefined}
                  data-testid="ownership-capacity-detail"
                />
              </>
            )}

            <div className={styles.owners} role="group" aria-labelledby={`${uid}-owners`}>
              <p className={styles.label} id={`${uid}-owners`}>Other owners on title (optional)</p>
              <p className={styles.hint}>
                Anyone else who owns the property with you, as shown on your deed or tax bill.
                If you&apos;re married, your spouse may need to sign the sale documents even
                if they aren&apos;t on title, for example when the property is your home.
                Your title company or attorney will confirm who must sign.
              </p>
              {otherOwners.map((name, idx) => (
                <div className={styles.ownerRow} key={idx}>
                  <input
                    className={styles.input}
                    type="text"
                    maxLength={255}
                    value={name}
                    aria-label={`Other owner ${idx + 1} full name`}
                    onChange={(e) =>
                      setOtherOwners((prev) => prev.map((n, i) => (i === idx ? e.target.value : n)))
                    }
                    data-testid={`ownership-other-owner-${idx}`}
                  />
                  <button
                    type="button"
                    className={styles.linkBtn}
                    onClick={() => setOtherOwners((prev) => prev.filter((_, i) => i !== idx))}
                    aria-label={`Remove other owner ${idx + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {otherOwners.length < MAX_OTHER_OWNERS && (
                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={() => setOtherOwners((prev) => [...prev, ''])}
                  data-testid="ownership-add-owner"
                >
                  + Add another owner
                </button>
              )}
            </div>
          </fieldset>

          {/* 2 — Proof */}
          <fieldset className={styles.group}>
            <legend className={styles.groupTitle}><span className={styles.num} aria-hidden="true">2</span> Proof of ownership</legend>
            <label className={styles.label} htmlFor={ids.proofType}>Document type *</label>
            <select
              id={ids.proofType}
              className={styles.select}
              value={proofType}
              onChange={(e) => setProofType(e.target.value)}
              aria-invalid={showErrors && !proofType ? 'true' : undefined}
              data-testid="ownership-proof-type"
            >
              <option value="">Choose one…</option>
              {PROOF_DOCUMENT_TYPES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <label className={styles.label} htmlFor={ids.proofFile}>Upload the document * <span className={styles.hintInline}>PDF, JPEG or PNG · up to {MAX_FILE_MB} MB</span></label>
            <input
              id={ids.proofFile}
              ref={proofInputRef}
              className={styles.file}
              type="file"
              accept={ACCEPT_ATTRIBUTE}
              onChange={(e) => handleProofFile(e.target.files && e.target.files[0])}
              aria-invalid={fileErrors.proof || (showErrors && !proofFile) ? 'true' : undefined}
              aria-describedby={fileErrors.proof ? `${ids.proofFile}-err` : undefined}
              data-testid="ownership-proof-file"
            />
            {fileErrors.proof && (
              <p className={styles.fieldError} id={`${ids.proofFile}-err`} role="alert">{fileErrors.proof}</p>
            )}
            {proofFile && <p className={styles.fileChosen}>Selected: {proofFile.name}</p>}
          </fieldset>

          {/* 3 — Authority (only when acting for someone else) */}
          {authorityNeeded && (
            <fieldset className={styles.group} data-testid="ownership-authority">
              <legend className={styles.groupTitle}><span className={styles.num} aria-hidden="true">3</span> Your authority to sell</legend>
              <p className={styles.hint}>{AUTHORITY_REQUIREMENTS[capacity]}</p>
              {authorityRows.map((row, idx) => (
                <div className={styles.authorityRow} key={row.id}>
                  <div className={styles.authorityFields}>
                    <label className={styles.label} htmlFor={`${uid}-${row.id}-type`}>Document {idx + 1} type</label>
                    <select
                      id={`${uid}-${row.id}-type`}
                      className={styles.select}
                      value={row.documentType}
                      onChange={(e) =>
                        setAuthorityRows((rows) =>
                          rows.map((r) => (r.id === row.id ? { ...r, documentType: e.target.value } : r)),
                        )
                      }
                    >
                      {AUTHORITY_DOCUMENT_TYPES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <label className={styles.label} htmlFor={`${uid}-${row.id}-file`}>Document {idx + 1} file</label>
                    <input
                      id={`${uid}-${row.id}-file`}
                      className={styles.file}
                      type="file"
                      accept={ACCEPT_ATTRIBUTE}
                      onChange={(e) => handleAuthorityFile(row.id, e.target.files && e.target.files[0], e.target)}
                      aria-invalid={fileErrors[row.id] ? 'true' : undefined}
                      data-testid={`ownership-authority-file-${idx}`}
                    />
                    {fileErrors[row.id] && <p className={styles.fieldError} role="alert">{fileErrors[row.id]}</p>}
                  </div>
                  {authorityRows.length > 1 && (
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => {
                        setFileError(row.id, null);
                        setAuthorityRows((rows) => rows.filter((r) => r.id !== row.id));
                      }}
                      aria-label={`Remove authority document ${idx + 1}`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {authorityRows.length < MAX_AUTHORITY_FILES && (
                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={() => setAuthorityRows((rows) => [...rows, newAuthorityRow(capacity)])}
                  data-testid="ownership-add-authority"
                >
                  + Add another document
                </button>
              )}
            </fieldset>
          )}

          <div className={styles.legal}>
            <p className={styles.copy}>
              <strong>{SELLER_HOW_WE_CHECK.lead}</strong> {SELLER_HOW_WE_CHECK.body}{' '}
              <strong>{SELLER_HOW_WE_CHECK.emphasis}</strong>
              {SELLER_HOW_WE_CHECK.tail}
            </p>
            <LeadParagraph lead={SELLER_PROTECT_INFO.lead}>{SELLER_PROTECT_INFO.body}</LeadParagraph>
            <LeadParagraph lead={SELLER_DOCUMENT_HANDLING.lead}>{SELLER_DOCUMENT_HANDLING.body}</LeadParagraph>
          </div>

          {isPoa && (
            <div className={styles.attest} data-testid="ownership-poa-confirmation-row">
              <input
                id={ids.poa}
                type="checkbox"
                checked={poaConfirmed}
                onChange={(e) => setPoaConfirmed(e.target.checked)}
                aria-invalid={showErrors && !poaConfirmed ? 'true' : undefined}
                required
                data-testid="ownership-poa-confirmation"
              />
              <label htmlFor={ids.poa}>{POA_CONFIRMATION_TEXT}</label>
            </div>
          )}

          <div className={styles.attest}>
            <input
              id={ids.attest}
              type="checkbox"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
              aria-invalid={showErrors && !attested ? 'true' : undefined}
              data-testid="ownership-attestation"
            />
            <label htmlFor={ids.attest}>{SELLER_ATTESTATION_TEXT}</label>
          </div>

          <p className={styles.disclaimer}>{LEGAL_DISCLAIMER}</p>

          {showErrors && validationProblems.length > 0 && (
            <ul className={styles.problems} id={ids.problems} role="alert" data-testid="ownership-problems">
              {validationProblems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
          {submitError && (
            <p className={styles.submitError} role="alert" data-testid="ownership-submit-error">{submitError}</p>
          )}

          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={submitting}
            aria-busy={submitting ? 'true' : undefined}
            data-testid="ownership-submit"
          >
            {submitting ? (
              <><span className={styles.spinner} aria-hidden="true" /> Checking your documents…</>
            ) : (
              'Submit documents for review'
            )}
          </button>
          {submitting && (
            <p className={styles.hint}>This can take up to a minute. Please keep this page open.</p>
          )}
        </form>
      )}
    </div>
  );
}
